import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import MUIDataTable from "mui-datatables";
import { Grid, Slide, Typography } from "@material-ui/core";
import BarChartIcon from '@material-ui/icons/BarChart';
import moment from "moment";

type OwnProps = {
    dischargeType: string;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type DrillDownDischargeProps = OwnProps & StateProps & DispatchProps;

const tableColumns = [
    {
        name: "Patient",
        options: { filter: false }
    },
    {
        name: "Admit Date",
        options: { filter: false }
    },
    {
        name: "Discharge Date",
        options: { filter: false }
    },
    {
        name: "Primary Care Provider",
        options: { filter: false }
    }
];

const tableOptions = {
    elevation: 0,
    filter: false,
    viewColumns: false,
    selectableRows: 'none' as const,
    selectableRowsOnClick: false,
    print: false,
    download: false,
    search: false,
    setRowProps: () => ({
        style: { cursor: 'default' }
    }),
};

const DrillDownDischarge = (props: DrillDownDischargeProps) => {
    const {
        DischargeDispositionStats,
        Patients,
        userPracticeCoordinatorProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestPatientsByPracticeIdWithDays,
        requestDischargeDispositionStatsForProvider,
        dischargeType,
    } = props;

    const [tokenState, setTokenState] = useState<APIStore.TokenType | undefined>();
    const [totalCount, setTotalCount] = useState<number>(0);
    const [dischargePatients, setDischargePatients] = useState<APIStore.ListPatient[]>([]);

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (userPracticeCoordinatorProfile) {
            let { isErrored, notFound, isLoaded } = userPracticeCoordinatorProfile;
            if ((!isErrored && !notFound && !isLoaded) && tokenState?.accessToken) {
                requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);
            } else {
                if (isLoaded && tokenState?.accessToken) {
                    let coordinatorProfile = userPracticeCoordinatorProfile.coordinatorProfile;

                    if (!Patients.isLoaded && !Patients.isLoading) {
                        requestPatientsByPracticeIdWithDays(
                            coordinatorProfile.defaultPractice.practiceId,
                            coordinatorProfile.practiceCareCoordinatorId,
                            (30 * 6),
                            tokenState?.accessToken
                        );
                    }
                }
            }
        }
    }, [tokenState, userPracticeCoordinatorProfile.coordinatorProfile, Patients]);

    useEffect(() => {
        if (tokenState?.accessToken && !DischargeDispositionStats.isLoaded && !DischargeDispositionStats.isLoading) {
            requestDischargeDispositionStatsForProvider(tokenState.accessToken);
        }
    }, [tokenState, DischargeDispositionStats]);

    useEffect(() => {
        if (DischargeDispositionStats.dischargeDispositions) {
            const match = DischargeDispositionStats.dischargeDispositions.find(
                (d: APIStore.DischargeDispositionStatistics) => d.typeDesciption === dischargeType
            );
            setTotalCount(match ? match.patientCount : 0);
        }
    }, [DischargeDispositionStats, dischargeType]);

    useEffect(() => {
        if (Patients?.patientsByPracticeId) {
            setDischargePatients(
                Patients.patientsByPracticeId.filter(
                    (patient: APIStore.ListPatient) => patient.dischargeDisposition == dischargeType
                )
            );
        }
    }, [Patients?.patientsByPracticeId]);

    const tableData = dischargePatients.map((patient: APIStore.ListPatient) => [
        `${patient.firstName} ${patient.lastName}`,
        patient.admitDate ? moment(patient.admitDate).format("MM-DD-YYYY") : 'N/A',
        patient.dischargeDate ? moment(patient.dischargeDate).format("MM-DD-YYYY") : 'N/A',
        patient.primaryCareProvider
    ]);

    const isPatientsLoading = !Patients.isLoaded || Patients.isLoading

    return (
        <Grid container direction="column" spacing={3}>
            <Grid item xs={12}>
                <Typography variant="subtitle1">Total Patients: {totalCount}</Typography>
            </Grid>

            <Grid item xs={12}>
                {isPatientsLoading ?
                    <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled" />
                    :
                    <MUIDataTable
                        title=""
                        data={tableData}
                        columns={tableColumns}
                        options={tableOptions}
                    />
            }
            </Grid>
        </Grid>
    );
}

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(DrillDownDischarge as any);
